import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

const fontsVfs = (pdfFonts as unknown as { pdfMake?: { vfs?: Record<string, string> }; vfs?: Record<string, string> });
const vfsData = fontsVfs?.pdfMake?.vfs ?? fontsVfs?.vfs;
if (vfsData && !(pdfMake as unknown as { vfs?: Record<string, string> }).vfs) {
  (pdfMake as unknown as { vfs: Record<string, string> }).vfs = vfsData;
}

export function generateAndDownloadSecurityDocPdf(): Promise<void> {
  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [30, 35, 30, 35],
    info: {
      title: 'TAU Engineering Forms - Security Architecture',
      author: 'Faculty of Engineering - Tel Aviv University',
      subject: 'Security & Form Defense Specifications',
    },
    styles: {
      mainHeader: { fontSize: 18, bold: true, color: '#111827', margin: [0, 0, 0, 4] },
      subHeader: { fontSize: 11, color: '#4b5563', margin: [0, 0, 0, 15] },
      sectionTitle: { fontSize: 13, bold: true, color: '#1e3a8a', margin: [0, 12, 0, 6] },
      bodyText: { fontSize: 9.5, color: '#374151', lineHeight: 1.35 },
      tableHeader: { fontSize: 9, bold: true, color: '#ffffff', fillColor: '#1e3a8a' },
      tableCell: { fontSize: 8.5, color: '#1f2937' },
    },
    content: [
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                text: 'TEL AVIV UNIVERSITY · FACULTY OF ENGINEERING\nApplication Forms Security & Cyber Protection Specifications',
                fillColor: '#f0fdf4',
                color: '#166534',
                bold: true,
                fontSize: 10,
                alignment: 'center',
                margin: [0, 4, 0, 4],
              },
            ],
          ],
        },
        margin: [0, 0, 0, 12],
      },
      { text: 'Form Security & Defense Architecture', style: 'mainHeader', alignment: 'center' },
      {
        text: 'Comprehensive 5-Layer Cyber Defense & Data Integrity Protocol · Updated October 2026',
        style: 'subHeader',
        alignment: 'center',
      },

      { text: 'Layer 1: Input Sanitization & Special Characters Escaping', style: 'sectionTitle' },
      {
        ul: [
          'Automatic removal of dangerous HTML tags (<script>, <iframe>, <style>) and control characters.',
          'Safe parameterization via GraphQL Variables to eliminate injection risks on monday.com boards.',
          'Whitespace trimming and compression across all text inputs.',
        ],
        style: 'bodyText',
      },

      { text: 'Layer 2: Strict Schema Validation (Zod Validation Engine)', style: 'sectionTitle' },
      {
        ul: [
          'Identification: Supports optional leading letter (e.g. P12345678) followed by 4-15 digits with automatic whitespace and dash removal.',
          'Name Fields: Physical input limit of 10 characters (maxLength=10) on first name, last name, and English names.',
          'Course Numbers: Strictly enforced numeric digits only.',
          'Email Validation: Standard RFC structure compliance check.',
          'Non-intrusive feedback: Errors highlighted only when moving to next step or upon submission.',
        ],
        style: 'bodyText',
      },

      { text: 'Layer 3: Backend Rate Limiting (Spam & DoS Defense)', style: 'sectionTitle' },
      {
        ul: [
          'Submission Quota: Max 5 submissions per hour per IP / identifier across all forms.',
          'Verification & Check Quota: Max 20 requests per minute for OTP requests, quota lookups, and duplicate checks.',
          'Uniform Rate-Limit Alert: "Too many attempts made. Please wait a few minutes before trying again."',
        ],
        style: 'bodyText',
      },

      { text: 'Layer 4: Cloudflare Turnstile Bot Defense', style: 'sectionTitle' },
      {
        ul: [
          'Non-intrusive human verification widget embedded on review & submission step.',
          'Server-side siteverify validation directly with Cloudflare API before any monday board write.',
          'Site Key: 0x4AAAAAAFLGTay8lcCWkIHH | Managed mode.',
        ],
        style: 'bodyText',
      },

      { text: 'Layer 5: Two-Factor Identity Verification & Column Clearance', style: 'sectionTitle' },
      {
        ul: [
          'Strict dual-field matching: Both I.D./Passport and Email must match board records before OTP generation.',
          'Single-use 6-digit verification code with 10-minute expiry and 5-attempt brute-force lockout.',
          'Automated column cleanup: Immediately resets verification code, validity, signature, and approval status on board upon successful validation.',
        ],
        style: 'bodyText',
      },

      { text: '\nSecurity Matrix Overview', style: 'sectionTitle' },
      {
        table: {
          headerRows: 1,
          widths: ['25%', '20%', '30%', '25%'],
          body: [
            [
              { text: 'Protection Layer', style: 'tableHeader' },
              { text: 'Scope / Level', style: 'tableHeader' },
              { text: 'Defense Mechanism', style: 'tableHeader' },
              { text: 'Targeted Threat', style: 'tableHeader' },
            ],
            [
              { text: '1. Sanitization & Escaping', style: 'tableCell' },
              { text: 'Client & Server', style: 'tableCell' },
              { text: 'HTML stripping & GraphQL Variables', style: 'tableCell' },
              { text: 'XSS & GraphQL Injection', style: 'tableCell' },
            ],
            [
              { text: '2. Zod Validation', style: 'tableCell' },
              { text: 'Schema / Form', style: 'tableCell' },
              { text: '10-char name limits & Regex rules', style: 'tableCell' },
              { text: 'Data corruption & Invalid inputs', style: 'tableCell' },
            ],
            [
              { text: '3. Rate Limiting', style: 'tableCell' },
              { text: 'Backend / Server', style: 'tableCell' },
              { text: '5 subs/hr, 20 checks/min window', style: 'tableCell' },
              { text: 'Brute-force & DoS Spam', style: 'tableCell' },
            ],
            [
              { text: '4. Cloudflare Turnstile', style: 'tableCell' },
              { text: 'Client + Cloudflare API', style: 'tableCell' },
              { text: 'Managed CAPTCHA-free validation', style: 'tableCell' },
              { text: 'Automated Bot Submissions', style: 'tableCell' },
            ],
            [
              { text: '5. OTP & Isolation', style: 'tableCell' },
              { text: 'Board & Email flow', style: 'tableCell' },
              { text: '6-digit OTP & Column Auto-reset', style: 'tableCell' },
              { text: 'Identity theft & Replay attacks', style: 'tableCell' },
            ],
          ],
        },
        margin: [0, 4, 0, 15],
      },

      {
        text: 'Document certified for Faculty of Engineering Digital Systems · Tel Aviv University',
        fontSize: 8,
        color: '#6b7280',
        alignment: 'center',
        margin: [0, 15, 0, 0],
      },
    ],
  };

  return new Promise((resolve, reject) => {
    try {
      const pdf = pdfMake.createPdf(docDefinition as never);
      pdf.download('TAU_Engineering_Forms_Security_Specifications.pdf');
      resolve();
    } catch (err) {
      console.error('Failed to generate security doc PDF', err);
      reject(err);
    }
  });
}
